import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import IssueCardDetailsDialog from "./IssueCardDetailsDialog";
import AddIssueCardDialog from "./AddIssueCardDialog";

const IssueCard = ({ card, handleDragStart, handleDeleteCard, handleOpenEditDialog, isDragOver = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  
  // Simple check: if card status is "Closed", disable editing and interactions (similar to Done column)
  const isClosed = card?.cardStatus === 'Closed';
  
  // console.log("IssueCard received card:", card);

  // Menu handlers
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Edit
  const handleEdit = () => {
    handleOpenEditDialog(card);
    handleMenuClose();
  };

  // Delete
  const handleDelete = () => {
    //if (window.confirm(`Are you sure you want to delete ${card.issueSummary}?`)) {
      handleDeleteCard(card.id);
    
    handleMenuClose();
  };

  // Open details dialog
  const handleCardClick = () => setOpenDetailsDialog(true);
  const handleDetailsDialogClose = () => setOpenDetailsDialog(false);

  // Get RAG status color
  const getRAGStatusColor = (ragStatus) => {
    switch (ragStatus) {
      case 'Red':
        return '#EF4444';
      case 'Amber':
        return '#F59E0B';
      case 'Green':
        return '#10B981';
      default:
        return '#F59E0B'; // Default to Amber
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return '#d32f2f';
      case 'High':
        return '#f57c00';
      case 'Medium':
        return '#fbc02d';
      case 'Low':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  return (
    <>
      <Paper
        data-card-id={card.id}
        sx={{
          mb: 2,
          p: 0,
          borderRadius: 2,
          backgroundColor: '#E8EAE6',
          position: 'relative',
          cursor: isClosed ? 'default' : 'move',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.3s, transform 0.3s',
          border: isDragOver ? '2px dashed #1976d2' : 'none',
          opacity: isClosed ? 0.7 : 1,
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
            transform: isClosed ? 'none' : 'scale(1.02)',
          },
        }}
        draggable={!isClosed}
        onDragStart={!isClosed ? (event) => handleDragStart(event, card) : null}
      >
        {/* Top Section */}
        <Box
          sx={{
            backgroundColor: '#1976d2',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            height: '40px',
            width: "100%"
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: "40%"}}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                display: 'block',
                lineHeight: '1',
                color: 'white',
                marginTop: '0.2rem'
              }}
            >
              {card.cardId || 'N/A'}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" width="60%" justifyContent="flex-end" alignItems="center" gap={0.5}>
            {/* Priority Chip */}
            <Chip
              label={card?.priority || card?.templateData?.priority || 'N/A'}
              size="small"
              sx={{
                backgroundColor: getPriorityColor(card?.priority || card?.templateData?.priority),
                color: '#fff',
                fontSize: '0.75rem',
                borderRadius: '25px',
                padding: '1px 1px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
              }}
            />

            <IconButton
              onClick={handleMenuClick}
              disabled={isClosed}
              sx={{
                color: 'text.secondary',
                padding: '0.5rem',
              }}
            >
              <MoreVertIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleEdit} disabled={isClosed}>Edit</MenuItem>
              <MenuItem onClick={handleDelete} disabled={isClosed}>Delete</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Middle Section */}
        <Box         
          sx={{
            backgroundColor: '#87CEEB',
            flexGrow: 1,
            p: 1,
          }}
        >
          <Box onClick={handleCardClick} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              {card.summary || "No summary provided"}
            </Typography>
          </Box>
        </Box>

        {/* Bottom Section */}
        <Box
          sx={{
            backgroundColor: '#d3d3d3',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '20px',
          }}
        >
          {card.dueDate && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TimerRoundedIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.8rem' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {new Date(card.dueDate).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1}>
            {/* RAG Status Indicator - Box format */}
            <Tooltip title={`RAG Status: ${card?.templateData?.ragStatus || 'Amber'}`} arrow>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  maxWidth: 12,
                  maxHeight: 12,
                  flexShrink: 0,
                  borderRadius: '2px',
                  backgroundColor: getRAGStatusColor(card?.templateData?.ragStatus),
                  border: '1px solid #fff',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                }}
              />
            </Tooltip>

            <Tooltip title={card.assigneeName || card?.templateData?.assignedTo || 'Unassigned'} arrow>
              <Avatar sx={{ bgcolor: 'green', width: 17, height: 17, fontSize: '0.70rem' }}>
                {(card.assigneeName || card?.templateData?.assignedTo || '?').charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Issue Card Details Dialog */}
      <IssueCardDetailsDialog
        open={openDetailsDialog}
        onClose={handleDetailsDialogClose}
        card={card}
        onEdit={handleOpenEditDialog}
      />
    </>
  );
};

export default IssueCard;
